/**
 * client/src/context/QuotaContext.jsx
 * Tracks Claude token usage and rate limit state.
 *
 * Listens for "claude-usage" custom events (dispatched by generateArchitectReply).
 * Tracks token counts (input/output/total) from x-claude-* response headers.
 *  - Rate limit lock triggered by error.code === "rate_limit" with retryAfter seconds
 *  - Auto-unlocks after retryAfter countdown
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const QuotaContext = createContext(null);

export function QuotaProvider({ children }) {
  const [tokenUsage, setTokenUsage] = useState({ input: 0, output: 0, total: 0 });
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState(null); // seconds remaining
  const [retryAt, setRetryAt] = useState(null); // Date when unlock happens
  const countdownRef = useRef(null);

  // Listen for token usage events from generateArchitectReply
  useEffect(() => {
    const handler = (e) => {
      const { inputTokens, outputTokens, totalTokens } = e.detail ?? {};
      setTokenUsage((prev) => ({
        input: prev.input + (inputTokens ?? 0),
        output: prev.output + (outputTokens ?? 0),
        total: prev.total + (totalTokens ?? 0),
      }));
    };
    window.addEventListener("claude-usage", handler);
    return () => window.removeEventListener("claude-usage", handler);
  }, []);

  // Countdown timer when rate limited
  useEffect(() => {
    if (!rateLimited || !retryAt) return;

    countdownRef.current = setInterval(() => {
      const secsLeft = Math.ceil((retryAt - Date.now()) / 1000);
      if (secsLeft <= 0) {
        setRateLimited(false);
        setRetryAfter(null);
        setRetryAt(null);
        clearInterval(countdownRef.current);
      } else {
        setRetryAfter(secsLeft);
      }
    }, 1000);

    return () => clearInterval(countdownRef.current);
  }, [rateLimited, retryAt]);

  /** Call this when a rate_limit error is caught in useIdwiz / useArchitect */
  const triggerRateLimit = useCallback((seconds = 60) => {
    const unlockAt = Date.now() + seconds * 1000;
    setRateLimited(true);
    setRetryAfter(seconds);
    setRetryAt(unlockAt);
  }, []);

  /** Manual unlock (e.g. user clicks "Try again") */
  const clearRateLimit = useCallback(() => {
    setRateLimited(false);
    setRetryAfter(null);
    setRetryAt(null);
    clearInterval(countdownRef.current);
  }, []);

  const value = {
    tokenUsage,
    rateLimited,
    retryAfter,
    triggerRateLimit,
    clearRateLimit,
    isSendLocked: rateLimited,
  };

  return <QuotaContext.Provider value={value}>{children}</QuotaContext.Provider>;
}

export function useQuota() {
  const ctx = useContext(QuotaContext);
  if (!ctx) throw new Error("useQuota must be used inside QuotaProvider");
  return ctx;
}
